﻿import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import axios from "@/features/axiosHelper";
import { ConnectionTypes } from "@/components/Maintenance/MaintenanceTypes";

const WorkflowLookupsContext = createContext();

export const useWorkflowLookups = () => useContext(WorkflowLookupsContext);

export const WorkflowLookupsProvider = ({ children }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [workflowLookupsLoaded, setWorkflowLookupsLoaded] = useState(false);

  const [itemqualifiers, setItemQualifiers] = useState([]);

  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [customerConnections, setCustomerConnections] = useState([]);
  const [ERPConnections, setERPConnections] = useState([]);
  const [warehouseConnections, setWarehouseConnections] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setRows([]);
      const response = await axios.get("/workflowlookups");
      const dataWithId = Array.isArray(response.data)
        ? response.data.map((item, idx) => ({ ...item, id: idx + 1 }))
        : [];
      setWorkflowLookupsLoaded(true);
      setRows(dataWithId);
    } catch (e) {
      console.log(e.message);
    }
  }, []);

  const getItemQualifiers = useCallback(async (connectionId, artifactType) => {
    axios
      .get("/connectionartifactschema/item-qualifiers", {
        params: {
          connectionId: connectionId,
          artifactType: artifactType,
        },
      })
      .then((response) => {
        setItemQualifiers(response.data.values || []);
      })
      .catch((error) => {
        console.error("Error fetching item qualifiers: ", error);
      });
  }, []);

  const fetchAssociatedConnections = async ({ connectionId }) => {
      try {
        const response = await axios.get("/connectioncatalogartifacts/get-associated-connections", {
          params: {
            connectionId,
          },
        });
        // Extract and parse ArtifactData from each item
        const parsedData = response.data.map((item) => {
              let artifactObj = {};
              try {
                artifactObj = {
                  ConnectionId: item.ConnectionId,
                  ConnectionName: item.ConnectionName
                };
              } catch {
                artifactObj = {};
              }
              return artifactObj;
            });
        return parsedData;
      } catch (err) {
        throw new Error(err?.message || "Failed to fetch erp names.");
      }
  }

  const fetchCatalogArtifactData = useCallback(
    async ({ connectionId, catalogType }) => {
      try {
        const response = await axios.get("/connectioncatalogartifacts", {
          params: {
            connectionId,
            catalogType,
            referenceSort: true,
          },
        });
        // Extract and parse ArtifactData from each item
        const parsedData = Array.isArray(response.data)
          ? response.data.map((item) => {
              let artifactObj = {};
              try {
                artifactObj = JSON.parse(item.ArtifactData);
              } catch {
                artifactObj = {};
              }
              return artifactObj;
            })
          : [];
        return parsedData;
      } catch (err) {
        throw new Error(err?.message || "Failed to fetch catalog data.");
      }
    },
    []
  );

  const fetchConnections = useCallback(async () => {
    try {
      setCustomers([]);
      setCustomersLoaded(false);
      const response = await axios.get("/connections/connection-name");

      setCustomerConnections(
        getConnectionsByType(response.data, ConnectionTypes.Customer)
      );
      setERPConnections(
        getConnectionsByType(response.data, ConnectionTypes.ERP)
      );
      setWarehouseConnections(
        getConnectionsByType(response.data, ConnectionTypes.Warehouse)
      );

      setCustomers(response.data);
      setCustomersLoaded(true);
    } catch (e) {
      setCustomers([]);
      setCustomersLoaded(false);
      console.error("Error fetching connections: ", e);
    }
  }, []);

  const getConnectionsByType = (customers, connectionType) => {
    return customers
      .map((c) => {
        let parsedType = null;
        try {
          const props = JSON.parse(c.JSONProperties);
          parsedType = props.ConnectionType;
        } catch {
          console.log("Error parsing customers");
        }
        return {
          ...c,
          connectionType: parsedType,
        };
      })
      .filter((c) => c.connectionType === connectionType);
  };

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  return (
    <WorkflowLookupsContext.Provider
      value={{
        selectedTab,
        setSelectedTab,
        workflowLookupsLoaded,
        rows,
        setRows,
        fetchData,
        fetchCatalogArtifactData,
        fetchAssociatedConnections,
        itemqualifiers,
        getItemQualifiers,
        customers,
        customersLoaded,
        fetchConnections,
        customerConnections,
        ERPConnections,
        warehouseConnections,
      }}
    >
      {children}
    </WorkflowLookupsContext.Provider>
  );
};
